import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';

const IconArrow = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const Logo = () => (
  <svg
    className="w-8 h-8 mr-2"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" stroke="var(--primary-blue)" strokeWidth="2" />
    <path
      d="M7 12H17M12 7V17"
      stroke="var(--primary-red)"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

function LoanCalculator() {
  const [formData, setFormData] = useState({
    amount: '',
    product: 'logbook',
    term: '',
  });
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [applyFormData, setApplyFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [applyErrors, setApplyErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const modalRef = useRef(null);

  const getMonthlyInterestRate = () => {
    if (formData.product === 'logbook' || formData.product === 'asset') {
      const term = parseInt(formData.term) || 0;
      if (term === 1) return 10;
      if (term >= 2 && term <= 3) return 5;
      if (term >= 4 && term <= 24) return 4;
      return 0;
    }
    return 6;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'product' && value === 'lpo' ? { term: '3' } : {}),
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleApplyChange = (e) => {
    const { name, value } = e.target;
    setApplyFormData((prev) => ({ ...prev, [name]: value }));
    if (applyErrors[name]) {
      setApplyErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Loan amount must be a positive number';
    } else if (parseFloat(formData.amount) < 100000) {
      newErrors.amount = 'Loan amount must be at least KES 100,000';
    }
    if (!formData.term || parseInt(formData.term) <= 0) {
      newErrors.term = 'Loan term must be a positive number';
    } else if (parseInt(formData.term) < 1) {
      newErrors.term = 'Loan term must be at least 1 month';
    } else if ((formData.product === 'logbook' || formData.product === 'asset') && parseInt(formData.term) > 24) {
      newErrors.term = 'Loan term cannot exceed 24 months for Logbook Loan or Asset Financing';
    } else if (formData.product === 'lpo' && parseInt(formData.term) !== 3) {
      newErrors.term = 'LPO Financing term must be exactly 3 months';
    }
    return newErrors;
  };

  const validateApplyForm = () => {
    const newErrors = {};
    if (!applyFormData.fullName || applyFormData.fullName.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }
    if (!applyFormData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applyFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!applyFormData.phone || !/^\+?\d{10,12}$/.test(applyFormData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (10-12 digits)';
    }
    return newErrors;
  };

  const calculateLoan = (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const principal = parseFloat(formData.amount);
    const monthlyRate = getMonthlyInterestRate() / 100;
    const termMonths = parseInt(formData.term);

    if (formData.product === 'lpo') {
      const monthlyInterest = principal * monthlyRate;
      const totalInterest = monthlyInterest * 3;
      const totalAmountPayable = principal + totalInterest;
      setResult({
        firstMonthlyPayment: monthlyInterest.toFixed(2),
        secondMonthlyPayment: monthlyInterest.toFixed(2),
        thirdMonthlyPayment: (principal + monthlyInterest).toFixed(2),
        totalAmountPayable: totalAmountPayable.toFixed(2),
        monthlyRate: monthlyRate * 100,
      });
    } else {
      const totalInterest = principal * monthlyRate * termMonths;
      const totalAmountPayable = principal + totalInterest;
      const monthlyPayment = totalAmountPayable / termMonths;
      setResult({
        monthlyPayment: monthlyPayment.toFixed(2),
        totalAmountPayable: totalAmountPayable.toFixed(2),
        monthlyRate: monthlyRate * 100,
      });
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateApplyForm();
    if (Object.keys(formErrors).length > 0) {
      setApplyErrors(formErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...applyFormData,
        loanDetails: {
          principal: formData.amount,
          product: formData.product,
          term: formData.term,
          monthlyRate: result.monthlyRate,
          ...(formData.product === 'lpo'
            ? {
                firstMonthlyPayment: result.firstMonthlyPayment,
                secondMonthlyPayment: result.secondMonthlyPayment,
                thirdMonthlyPayment: result.thirdMonthlyPayment,
              }
            : { monthlyPayment: result.monthlyPayment }),
          totalAmountPayable: result.totalAmountPayable,
        },
      };
      const response = await axios.post(
        process.env.REACT_APP_CALCULATOR_URL || 'https://tglcredit.co.ke/api/calculator-email.php',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('Email sent successfully:', response.data);
      setShowSuccessMessage(true);
      setTimeout(() => {
        setIsModalOpen(false);
        setApplyFormData({ fullName: '', email: '', phone: '' });
        setShowSuccessMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      if (error.response) {
        setSubmitError(`Failed to send application: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        setSubmitError('Failed to send application. Please check your connection or server availability.');
      } else {
        setSubmitError('Failed to send application. An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setApplyErrors({});
    setApplyFormData({ fullName: '', email: '', phone: '' });
    setSubmitError('');
    setShowSuccessMessage(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isModalOpen]);

  return (
    <section className="bg-gradient-to-b from-gray-50 to-blue-50 py-16 sm:py-20 min-h-screen flex items-center">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <h2 className="text-xl sm:text-2xl font-inter font-bold text-primary-blue text-center mb-12 tracking-tight animate-fade-up flex items-center justify-center">
          <Logo />
          <span className="underline decoration-primary-red">Loan Calculator</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <form
            onSubmit={calculateLoan}
            className="space-y-6 animate-fade-up bg-white/80 backdrop-blur-md p-6 rounded-lg shadow-lg"
            style={{ animationDelay: '0.2s' }}
          >
            <div>
              <label
                htmlFor="amount"
                className="block text-sm font-inter font-semibold text-primary-blue mb-2"
              >
                Loan Amount (KES)
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                className={`w-full px-4 py-3 text-sm border ${
                  errors.amount ? 'border-primary-red' : 'border-primary-blue/50'
                } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300`}
                placeholder="Enter loan amount (min 100,000)"
                aria-describedby={errors.amount ? 'amount-error' : undefined}
                required
                min="100000"
              />
              {errors.amount && (
                <p id="amount-error" className="text-primary-red text-sm mt-1" role="alert">
                  {errors.amount}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="product"
                className="block text-sm font-inter font-semibold text-primary-blue mb-2"
              >
                Select a product
              </label>
              <select
                id="product"
                name="product"
                value={formData.product}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm border border-primary-blue/50 rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300"
              >
                <option value="logbook">Logbook Loan</option>
                <option value="asset">Asset Financing</option>
                <option value="lpo">LPO Financing</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="term"
                className="block text-sm font-inter font-semibold text-primary-blue mb-2"
              >
                Loan Term (Months)
              </label>
              <input
                type="number"
                id="term"
                name="term"
                value={formData.term}
                onChange={handleChange}
                className={`w-full px-4 py-3 text-sm border ${
                  errors.term ? 'border-primary-red' : 'border-primary-blue/50'
                } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300`}
                placeholder={formData.product === 'lpo' ? 'Fixed at 3 months' : 'Enter loan term (min 1)'}
                aria-describedby={errors.term ? 'term-error' : undefined}
                required
                min="1"
                max={formData.product === 'logbook' || formData.product === 'asset' ? '24' : '3'}
                disabled={formData.product === 'lpo'}
              />
              {errors.term && (
                <p id="term-error" className="text-primary-red text-sm mt-1" role="alert">
                  {errors.term}
                </p>
              )}
            </div>
            <div className="text-sm font-inter font-medium text-gray-600">
              <span>Monthly Interest Rate: </span>
              <span className="font-bold">{getMonthlyInterestRate().toFixed(1)}%</span>
            </div>
            <button
              type="submit"
              className="group inline-flex bg-primary-blue text-white font-inter font-medium px-6 py-3 rounded-md hover:bg-primary-red hover:shadow-lg hover:shadow-primary-red/30 transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2"
            >
              <span className="flex items-center">
                Calculate
                <span className="ml-2 rounded-full bg-white border border-[--primary-blue] group-hover:border-[--primary-red] p-1 flex items-center justify-center">
                  <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--primary-blue] group-hover:text-[--primary-blue]" />
                </span>
              </span>
            </button>
          </form>
          {result && (
            <div
              className="bg-white/95 backdrop-blur-md p-6 rounded-lg shadow-lg border border-primary-blue/10 animate-fade-up"
              style={{ animationDelay: '0.3s' }}
              aria-describedby={Object.keys(errors).length > 0 ? 'general-error' : undefined}
            >
              <h3 className="text-lg font-inter font-bold text-primary-blue mb-4 tracking-tight">
                Financing Estimate
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                  <dt>Principal</dt>
                  <dd className="font-bold">KES {parseFloat(formData.amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                </div>
                <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                  <dt>Product</dt>
                  <dd className="font-bold">
                    {formData.product === 'logbook' ? 'Logbook Loan' : formData.product === 'asset' ? 'Asset Financing' : 'LPO Financing'}
                  </dd>
                </div>
                <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                  <dt>Monthly Interest Rate</dt>
                  <dd className="font-bold">{result.monthlyRate.toFixed(1)}%</dd>
                </div>
                <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                  <dt>Loan Term</dt>
                  <dd className="font-bold">{formData.term} Months</dd>
                </div>
                {formData.product === 'lpo' ? (
                  <>
                    <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                      <dt>1st Monthly Payable</dt>
                      <dd className="font-bold">KES {parseFloat(result.firstMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                    </div>
                    <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                      <dt>2nd Monthly Payable</dt>
                      <dd className="font-bold">KES {parseFloat(result.secondMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                    </div>
                    <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                      <dt>3rd Monthly Payable</dt>
                      <dd className="font-bold">
                        KES {parseFloat(result.thirdMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                      </dd>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                    <dt>Monthly Payable</dt>
                    <dd className="font-bold">KES {parseFloat(result.monthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                  </div>
                )}
                <div className="flex justify-between text-[13px] font-inter font-medium text-gray-600">
                  <dt>Total Amount Payable</dt>
                  <dd className="font-bold">KES {parseFloat(result.totalAmountPayable).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                </div>
              </dl>
              <button
                onClick={() => setIsModalOpen(true)}
                className="group inline-flex bg-primary-blue text-white font-inter font-medium px-6 py-3 rounded-md hover:bg-primary-red hover:shadow-lg hover:shadow-primary-red/30 transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 mt-4"
              >
                <span className="flex items-center">
                  Apply Now
                  <span className="ml-2 rounded-full bg-white border border-[--primary-blue] group-hover:border-[--primary-red] p-1 flex items-center justify-center">
                    <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--primary-blue] group-hover:text-[--primary-blue]" />
                  </span>
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true">
          <div
            ref={modalRef}
            className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-inter font-bold text-primary-blue tracking-tight text-center w-full">
                Apply for Financing
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-600 hover:text-primary-red focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2"
                aria-label="Close modal"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            <hr className="border-t border-primary-blue/20 my-4" />
            {showSuccessMessage ? (
              <div
                className="bg-green-100 text-green-800 p-4 rounded-lg mb-4 animate-fade-in"
                role="alert"
                aria-live="polite"
              >
                <h4 className="text-base font-inter font-semibold text-green-800">
                  Application Sent Successfully!
                </h4>
                <p className="text-sm font-inter text-green-700">
                  Your application has been successfully sent to info@tglcredit.co.ke! We will contact you soon.
                </p>
              </div>
            ) : (
              <>
                {submitError && (
                  <div
                    className="bg-red-100 text-red-800 p-3 rounded-lg mb-4 animate-fade-in"
                    role="alert"
                    aria-live="polite"
                  >
                    {submitError}
                  </div>
                )}
                {result && (
                  <>
                    <h4 className="text-md font-inter font-semibold text-primary-blue mb-4">
                      Financing Estimate
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="text-[13px] font-inter font-medium text-gray-600">
                        <dt>Principal</dt>
                        <dd className="font-bold">KES {parseFloat(formData.amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                      </div>
                      <div className="text-[13px] font-inter font-medium text-gray-600">
                        <dt>Product</dt>
                        <dd className="font-bold">
                          {formData.product === 'logbook' ? 'Logbook Loan' : formData.product === 'asset' ? 'Asset Financing' : 'LPO Financing'}
                        </dd>
                      </div>
                      <div className="text-[13px] font-inter font-medium text-gray-600">
                        <dt>Monthly Interest Rate</dt>
                        <dd className="font-bold">{result.monthlyRate.toFixed(1)}%</dd>
                      </div>
                      <div className="text-[13px] font-inter font-medium text-gray-600">
                        <dt>Loan Term</dt>
                        <dd className="font-bold">{formData.term} Months</dd>
                      </div>
                      {formData.product === 'lpo' ? (
                        <>
                          <div className="text-[13px] font-inter font-medium text-gray-600">
                            <dt>1st Monthly Payable</dt>
                            <dd className="font-bold">KES {parseFloat(result.firstMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                          </div>
                          <div className="text-[13px] font-inter font-medium text-gray-600">
                            <dt>2nd Monthly Payable</dt>
                            <dd className="font-bold">KES {parseFloat(result.secondMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                          </div>
                          <div className="text-[13px] font-inter font-medium text-gray-600">
                            <dt>3rd Monthly Payable</dt>
                            <dd className="font-bold">
                              KES {parseFloat(result.thirdMonthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                            </dd>
                          </div>
                        </>
                      ) : (
                        <div className="text-[13px] font-inter font-medium text-gray-600">
                          <dt>Monthly Payable</dt>
                          <dd className="font-bold">KES {parseFloat(result.monthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                        </div>
                      )}
                      <div className="text-[13px] font-inter font-medium text-gray-600">
                        <dt>Total Amount Payable</dt>
                        <dd className="font-bold">KES {parseFloat(result.totalAmountPayable).toLocaleString('en-US', { minimumFractionDigits: 0 })}</dd>
                      </div>
                    </div>
                  </>
                )}
                <form onSubmit={handleApplySubmit} className="space-y-6" noValidate>
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-inter font-semibold text-primary-blue mb-2"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={applyFormData.fullName}
                      onChange={handleApplyChange}
                      className={`w-full px-4 py-3 text-sm border ${
                        applyErrors.fullName ? 'border-primary-red' : 'border-primary-blue/50'
                      } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300`}
                      placeholder="Enter your full name"
                      aria-describedby={applyErrors.fullName ? 'fullName-error' : undefined}
                      required
                    />
                    {applyErrors.fullName && (
                      <p id="fullName-error" className="text-primary-red text-sm mt-1" role="alert">
                        {applyErrors.fullName}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-inter font-semibold text-primary-blue mb-2"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={applyFormData.email}
                        onChange={handleApplyChange}
                        className={`w-full px-4 py-3 text-sm border ${
                          applyErrors.email ? 'border-primary-red' : 'border-primary-blue/50'
                        } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300`}
                        placeholder="Enter your email address"
                        aria-describedby={applyErrors.email ? 'email-error' : undefined}
                        required
                      />
                      {applyErrors.email && (
                        <p id="email-error" className="text-primary-red text-sm mt-1" role="alert">
                          {applyErrors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-inter font-semibold text-primary-blue mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={applyFormData.phone}
                        onChange={handleApplyChange}
                        className={`w-full px-4 py-3 text-sm border ${
                          applyErrors.phone ? 'border-primary-red' : 'border-primary-blue/50'
                        } rounded-lg shadow-sm focus:outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/50 transition-all duration-300`}
                        placeholder="Enter your phone number (e.g., +254123456789)"
                        aria-describedby={applyErrors.phone ? 'phone-error' : undefined}
                        required
                      />
                      {applyErrors.phone && (
                        <p id="phone-error" className="text-primary-red text-sm mt-1" role="alert">
                          {applyErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="group inline-flex bg-gray-200 text-primary-blue font-inter font-medium px-6 py-3 rounded-md hover:bg-gray-300 hover:text-primary-red transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="group inline-flex bg-primary-blue text-white font-inter font-medium px-6 py-3 rounded-md hover:bg-primary-red hover:shadow-lg hover:shadow-primary-red/30 transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 flex items-center gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <span className="ml-2 rounded-full bg-white border border-[--primary-blue] group-hover:border-[--primary-red] p-1 flex items-center justify-center">
                            <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--primary-blue] group-hover:text-[--primary-blue]" />
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      <style>
        {`
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-up {
            animation: fade-up 0.6s ease-out forwards;
            willChange: transform, opacity;
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.8s ease-in-out forwards;
            willChange: opacity;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-up, .animate-fade-in, .hover\\:scale-105, .hover\\:shadow-lg, .group-hover\\:opacity-50, .group-hover\\:translate-x-1, .group-hover\\:border-[--primary-red] {
              animation: none !important;
              transform: none !important;
              opacity: 1 !important;
              transition: none !important;
            }
          }
          :root {
            --primary-blue: #2F498C;
            --primary-red: #843345;
            --primary-red-dark: #672737;
          }
          .bg-primary-blue {
            background-color: var(--primary-blue);
          }
          .bg-primary-red {
            background-color: var(--primary-red);
          }
          .text-primary-blue {
            color: var(--primary-blue);
          }
          .text-primary-red {
            color: var(--primary-red);
          }
          .text-primary-red-dark {
            color: var(--primary-red-dark);
          }
          .decoration-primary-red {
            text-decoration-color: var(--primary-red);
          }
          .hover\\:bg-primary-blue:hover {
            background-color: var(--primary-blue);
          }
          .hover\\:bg-primary-red:hover {
            background-color: var(--primary-red);
          }
          .hover\\:text-primary-red:hover {
            color: var(--primary-red);
          }
          .hover\\:shadow-primary-red\\/30:hover {
            --tw-shadow-color: rgba(132, 51, 69, 0.3);
            --tw-shadow: var(--tw-shadow-colored);
          }
          .border-primary-blue\\/10 {
            border-color: rgba(47, 73, 140, 0.1);
          }
          .border-primary-blue\\/20 {
            border-color: rgba(47, 73, 140, 0.2);
          }
          .border-primary-blue\\/50 {
            border-color: rgba(47, 73, 140, 0.5);
          }
          .group-hover\\:text-primary-red-dark:hover {
            color: var(--primary-red-dark);
          }
          .group-hover\\:border-[--primary-red]:hover {
            border-color: var(--primary-red);
          }
          .focus\\:ring-primary-blue:focus {
            --tw-ring-color: var(--primary-blue);
          }
          @media (max-width: 640px) {
            h2 {
              font-size: 1.25rem;
              line-height: 1.75rem;
            }
          }
        `}
      </style>
    </section>
  );
}

export default LoanCalculator;